import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { getEntries, getRaceLeaderboard } from '../api';
import ScoreTable from './ScoreTable';
import VideoOverlay from './VideoOverlay';

const action = { padding: '8px 14px', borderRadius: 4, border: '1px solid #444',
  background: '#222', color: '#eee', cursor: 'pointer', fontSize: 13 };
const date = value => value ? new Date(value).toLocaleString('sv-SE') : 'Not recorded';

// The race prop comes exclusively from the authenticated admin race list.
// Viewing an archived video does not change its public visibility.
export default function AdminRaceDetails({ race }) {
  const [showVideo, setShowVideo] = useState(false);
  const [entries, setEntries] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getEntries(race.id), getRaceLeaderboard(race.id)])
      .then(([nextEntries, nextScores]) => {
        if (active) { setEntries(nextEntries); setScores(nextScores); }
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [race.id, race.posted_at, attempt]);

  return (
    <section id={`race-details-${race.id}`} aria-label={`${race.name} details`}
      style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #333', color: '#ccc' }}>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13, margin: '0 0 16px' }}>
        <dt>Circuit</dt><dd style={{ margin: 0 }}>{race.circuit} · {race.country}</dd>
        <dt>Race start</dt><dd style={{ margin: 0 }}>{date(race.race_date)}</dd>
        <dt>Results posted</dt><dd style={{ margin: 0 }}>{date(race.posted_at)}</dd>
        <dt>Race code</dt><dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{race.entry_code || 'No code set'}</dd>
      </dl>

      <h3 style={{ fontSize: 15, marginBottom: 10 }}>Pre-race video</h3>
      {race.video_url ? (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <button type="button" style={{ ...action, background: '#f9b000', color: '#111' }} onClick={() => setShowVideo(true)}>
            ▶ Play saved video
          </button>
          <span style={{ fontSize: 12, color: '#999' }}>{race.video_public ? 'Public on race page' : 'Admin only'}</span>
        </div>
      ) : <p style={{ color: '#999', fontSize: 13 }}>No pre-race video saved for this race.</p>}
      {showVideo && race.video_url && createPortal(
        <VideoOverlay url={race.video_url} onClose={() => setShowVideo(false)} />, document.body
      )}

      <p style={{ marginTop: 16 }}><Link to={`/races/${race.id}`} style={{ color: '#f9b000', fontSize: 13 }}>Open race page →</Link></p>

      {loading ? <p role="status">Loading predictions and scores…</p> : error ? (
        <div role="alert">
          <p>Could not load predictions and scores.</p>
          <button type="button" style={action} onClick={() => setAttempt(value => value + 1)}>Retry</button>
        </div>
      ) : (
        <>
          <h3 style={{ fontSize: 15, marginTop: 20 }}>Race scores</h3>
          <p style={{ color: '#999', fontSize: 12 }}>All participants, including staff.</p>
          <ScoreTable rows={scores} />
          <h3 style={{ fontSize: 15, marginTop: 20 }}>Predictions ({entries.length})</h3>
          {entries.length ? (
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr><th style={{ textAlign: 'left', padding: 8 }}>Name</th><th style={{ textAlign: 'left', padding: 8 }}>Top 10 picks</th></tr></thead>
                <tbody>{entries.map(entry => (
                  <tr key={entry.id}>
                    <td style={{ padding: 8, verticalAlign: 'top', borderTop: '1px solid #222' }}>{entry.user_name}</td>
                    <td style={{ padding: 8, borderTop: '1px solid #222' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {entry.predictions.map((driver, index) => <span key={index} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>P{index + 1} {driver}</span>)}
                      </div>
                      <div style={{ marginTop: 6, color: '#999', fontSize: 11 }}>Submitted {date(entry.submitted_at)}</div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <p style={{ color: '#999', fontSize: 13 }}>No entries for this race.</p>}
        </>
      )}
    </section>
  );
}
